import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ILongSteelOrderItem } from '../../../../../store/ducks/long-steel-shopping-cart/types';
import { ApplicationState } from '../../../../../store';

import {
  Container,
  MenuTable,
  ProductNotFoundAlertContainer,
  HeaderContainer,
  Title,
  Subtitle
} from './styles';

import { Button, Icon } from '@gerdau/hefesto';
import { sendTagging } from '../../../../../store/ducks/tagging/actions';
import SearchedItemsCodeEnum from './enum/SearchedItemsCodeEnum';
import SpreadsheetTableMvp from '../SpreadsheetTableMvp';
import {
  ECodeType,
  IOrderEntryPreference
} from '../../../../../store/ducks/user/types';
import { RcFile } from 'antd/es/upload/interface';
import {
  cleanSpreadSheetMVP,
  spreadSheetMVP
} from '../../../../../store/ducks/long-steel-order-item-search/actions';
import { ICustomer } from '../../../../../store/ducks/customers/types';
import { SPREADSHEET_PRODUCT_NOT_FOUND } from '../../../../../store/ducks/long-steel-order-item-search/types';
import { addToast } from '../../../../../store/ducks/toast/actions';
import { EToastTypes } from '../../../../../store/ducks/toast/types';

interface SearchedListMvpProps {
  searchedItems: ILongSteelOrderItem[];
  foundItens: number;
  notFoundItens: number;
  onRemoveAllItems: () => void;
  onRemoveSearchedItem(productIndex: number): void;
  onAddSearchedItem(items: ILongSteelOrderItem[]): void;
  userOrderEntryPreference: IOrderEntryPreference;
  emitterId: string;
  searchingItemsSpreadsheetItemsExceeded?: boolean;
}

interface productTableProps {
  key: number;
  product: string;
  codProduct: string;
  description: string;
  quantity: string | number;
  measureUnit: string;
  desiredDate: string;
}

interface productErrorProps {
  key?: number;
  product?: string;
  message: string;
  code?: string;
  prefix?: string;
}

const SearchedListMvp: React.FC<SearchedListMvpProps> = ({
  searchedItems,
  onRemoveSearchedItem,
  onRemoveAllItems,
  userOrderEntryPreference,
  emitterId,
  searchingItemsSpreadsheetItemsExceeded
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [dataProducts, setDataProducts] = useState<any>([]);
  const [inputSheet, setInputSheet] = useState<any>();
  const [dataProductsErrors, setDataProductsErrors] = useState<
    productErrorProps[]
  >([]);

  const dispatch = useDispatch();

  const customersState = useSelector(
    (state: ApplicationState) => state.customers
  );

  const [codeIsInternal, setCodeIsInternal] = useState<boolean>(false);

  useEffect(() => {
    const products: productTableProps[] = [];
    const productsWithErrors: productErrorProps[] = [];

    searchedItems.forEach((item: ILongSteelOrderItem, index: number) => {
      const hasInternalCode =
        userOrderEntryPreference &&
        userOrderEntryPreference?.codeType === 'CLIENT_SKU' &&
        item.customerSku.length > 0;
      setCodeIsInternal(hasInternalCode);

      products.push({
        key: index,
        product: hasInternalCode ? item.customerSku : item.sku,
        codProduct: item.customerPurchaseItem?.length
          ? Number(item.customerPurchaseItem).toString()
          : '-',
        description: item.description?.length
          ? item.description
          : SPREADSHEET_PRODUCT_NOT_FOUND,
        quantity: item.quantity ? String(item.quantity) : '-',
        measureUnit: item.measureUnit?.length ? item.measureUnit : '-',
        desiredDate: item.desiredDate ?? '-'
      });

      if (
        !item.found &&
        (!item.description?.length ||
          item.description === SPREADSHEET_PRODUCT_NOT_FOUND)
      ) {
        productsWithErrors.push({
          key: index,
          product: item.sku,
          message: item.message,
          code: item.code ?? '10'
        });
      }
    });

    if (products.length === productsWithErrors.length) {
      dispatch(
        addToast({
          message:
            'Nenhum produto encontrado para os códigos de materiais informados.',
          type: EToastTypes.DANGER,
          durationInMS: 5000,
          spaceToTop: 80
        })
      );
      dispatch(
        sendTagging({
          event: 'nenhum_produto_encontrado',
          screenName: 'planilha',
          mensagem:
            'Nenhum produto encontrado para os códigos de materiais informados.'
        })
      );
      dispatch(cleanSpreadSheetMVP());
      return;
    }

    if (products.find((product) => product.codProduct.length > 10)) {
      productsWithErrors.push({
        key: productsWithErrors.length + 1,
        message:
          'Um ou mais produtos excedeu o limite de 10 caracteres do número da linha.',
        prefix: '',
        product: ''
      });
    }

    setDataProducts(products.slice(0, 30));
    setDataProductsErrors(productsWithErrors.slice(0, 30));
  }, [searchedItems]);

  const ProductNotFoundAlert = ({
    code,
    product = 'não identificado',
    message,
    prefix = 'O produto'
  }: {
    code?: string;
    product?: string;
    message: string;
    prefix?: string;
  }) => {
    const errorMsg =
      code === SearchedItemsCodeEnum.NOT_FOUND
        ? ' não foi encontrado.'
        : message;
    return (
      <ProductNotFoundAlertContainer>
        <Icon option="InfoCircle" variant="warning" size="sm" />
        {(product && `${prefix ?? ''} ${product}`.trim()) || ''}
        {errorMsg}
      </ProductNotFoundAlertContainer>
    );
  };

  const customerData: ICustomer = customersState.customers.find(
    (customer) => customer.customerId === emitterId
  ) as ICustomer;

  const handleUploadFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const onFileUploaded = (file: RcFile) => {
    if (userOrderEntryPreference) {
      dispatch(
        spreadSheetMVP(
          emitterId,
          ECodeType.SKU,
          file,
          userOrderEntryPreference,
          customerData.division,
          customerData.locality.state || ''
        )
      );
    }
    dispatch(
      sendTagging({
        etapa: 'planilha_carregada',
        screenName: 'enviar_planilha',
        event: 'planilha_carregada'
      })
    );
  };

  const HiddenInput = () => {
    return (
      <input
        key={JSON.stringify(userOrderEntryPreference)}
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".xls,.xlsx"
        value={inputSheet}
        onChange={(event) => {
          const files = event.target.files;
          if (files && files.length > 0) {
            setInputSheet(
              files[0].name + files[0].lastModified + new Date().getTime()
            );
            onFileUploaded(files[0] as RcFile);
          }
        }}
      />
    );
  };

  useEffect(() => {
    const msg =
      'Sua planilha excedeu o limite de produtos. Foram adicionados na tabela os 30 primeiros.';
    if (
      searchingItemsSpreadsheetItemsExceeded &&
      !dataProductsErrors.find(({ message }) => message === msg)
    ) {
      dispatch(
        sendTagging({
          event: 'limite_produtos_excedido',
          screenName: 'enviar_planilha',
          etapa: 'planilha_carregada'
        })
      );

      setDataProductsErrors([
        ...dataProductsErrors,
        {
          key: dataProductsErrors.length + 1,
          message: msg,
          prefix: '',
          product: ''
        }
      ]);
    }
  }, [searchingItemsSpreadsheetItemsExceeded, dataProductsErrors]);

  return (
    <Container>
      <HeaderContainer>
        <Title>Sua planilha</Title>

        <Subtitle>
          Você poderá alterar os dados dos produtos depois que avançar para o
          carrinho.
        </Subtitle>

        {dataProductsErrors.map((data: productErrorProps, index: number) => (
          <ProductNotFoundAlert
            key={index}
            code={data.code}
            message={data.message}
            product={data?.product}
          />
        ))}
      </HeaderContainer>
      <MenuTable>
        <Button
          onClick={handleUploadFile}
          name="removeAllItems"
          ariaLabel="removeAllItems"
          variant="secondary"
        >
          Reenviar planilha
        </Button>
        <Button
          onClick={onRemoveAllItems}
          name="removeAllItems"
          ariaLabel="removeAllItems"
          variant="danger"
        >
          Excluir todos os produtos
        </Button>
      </MenuTable>
      <SpreadsheetTableMvp
        onRemoveSearchedItem={onRemoveSearchedItem}
        products={dataProducts}
        codeIsInternal={codeIsInternal}
      />
      <HiddenInput />
    </Container>
  );
};

export default SearchedListMvp;
